import { createStyles, Input, rem, Text } from '@mantine/core';
import Mention from '@tiptap/extension-mention';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { EditorContent, Extension, ReactRenderer, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import tippy, { Instance as TippyInstance } from 'tippy.js';
import MentionSuggestionList, {
  MentionItem,
  MentionSuggestionListRef,
} from 'components/inputs/text-expression/MentionSuggestionList';
import { useEffect, useRef } from 'react';
import removeAccents from 'remove-accents';

// ─── Serialisation helpers ────────────────────────────────────────────────────

/**
 * Converts a template string such as "Hello {firstName}!" into the TipTap
 * JSON content that the Mention extension understands.
 * The return type is intentionally `any` to satisfy TipTap's internal `Content`
 * union, which lives in a transitive package not directly listed as a dependency.
 */
export function parseTemplateToContent(template: string, items: MentionItem[]): any {
  const nodes = template.split(/(\{[^{}]+\})/).flatMap<any>((part) => {
    const match = part.match(/^\{([^{}]+)\}$/);
    if (match) {
      const id = match[1];
      const found = items.find((item) => item.id === id);
      return [{ type: 'mention', attrs: { id, label: found?.label ?? id } }];
    }
    return part ? [{ type: 'text', text: part }] : [];
  });

  return {
    type: 'doc',
    content: [{ type: 'paragraph', content: nodes }],
  };
}

/**
 * Reads the current editor state and produces a template string where every
 * mention node is serialized as `{fieldId}`.
 * The structural `editor` type is used to be compatible with both the
 * `@tiptap/core` Editor (received in `onUpdate`) and the `@tiptap/react` Editor
 * (returned by `useEditor`), which differ by one internal property.
 */
export function serializeEditorToTemplate(editor: {
  state: { doc: { descendants: (fn: (node: any) => void) => void } };
}): string {
  const parts: string[] = [];
  editor.state.doc.descendants((node: any) => {
    if (node.isText) parts.push(node.text ?? '');
    else if (node.type.name === 'mention') parts.push(`{${node.attrs.id}}`);
  });
  return parts.join('');
}

// ─── TipTap extension that blocks newlines ────────────────────────────────────

/**
 * Prevents the user from inserting newlines, keeping the expression single-line.
 * Priority 50 (below Mention's default 100) ensures TipTap registers the
 * Mention suggestion plugin BEFORE this keymap. ProseMirror processes plugins
 * in registration order, so the suggestion plugin gets first pick of Enter when
 * the dropdown is open; only when no suggestion is active does this keymap fire.
 */
const PreventNewLines = Extension.create({
  name: 'preventNewLines',
  priority: 50,
  addKeyboardShortcuts() {
    return {
      Enter: () => true,
      'Shift-Enter': () => true,
    };
  },
});

// ─── TipTap extension that highlights mention chips inside a selection ──────────

/**
 * Adds a `te-mention--selected` CSS class to every mention node whose
 * position is covered by the current text or node selection. This is
 * necessary because the chips have `user-select: none`, which prevents the
 * browser from applying its native `::selection` highlight to them.
 */
const MentionSelectionHighlight = Extension.create({
  name: 'mentionSelectionHighlight',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('mentionSelectionHighlight'),
        props: {
          decorations(state) {
            const { doc, selection } = state;
            const { from, to } = selection;
            if (from === to) return DecorationSet.empty;
            const decos: Decoration[] = [];
            doc.nodesBetween(from, to, (node, pos) => {
              if (node.type.name === 'mention') {
                decos.push(
                  Decoration.node(pos, pos + node.nodeSize, {
                    class: 'te-mention--selected',
                  })
                );
              }
            });
            return DecorationSet.create(doc, decos);
          },
        },
      }),
    ];
  },
});

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = createStyles((theme, { size }: { size: 'sm' | 'lg' }) => {
  const isLg = size === 'lg';
  return {
    editorWrapper: {
      border: `1px solid ${theme.colors.gray[3]}`,
      borderRadius: theme.radius.sm,
      padding: isLg ? `${rem(11)} ${rem(16)}` : `${rem(7)} ${rem(12)}`,
      minHeight: isLg ? rem(48) : rem(36),
      cursor: 'text',
      backgroundColor: theme.white,
      transition: 'border-color 100ms ease',
      '&:hover': {
        borderColor: theme.colors.gray[5],
      },
      '&:focus-within': {
        borderColor: theme.colors.red[6],
        outline: `2px solid ${theme.fn.rgba(theme.colors.red[6], 0.2)}`,
        outlineOffset: -2,
      },
      '.ProseMirror': {
        outline: 'none',
        fontFamily: theme.fontFamily,
        fontSize: isLg ? theme.fontSizes.md : theme.fontSizes.sm,
        color: theme.colors.dark[9],
        lineHeight: theme.lineHeight,
        '& > p': {
          margin: 0,
        },
      },
    },
    hint: {
      color: theme.colors.gray[5],
      fontSize: theme.fontSizes.xs,
      marginTop: rem(4),
    },
  };
});

// ─── Component ────────────────────────────────────────────────────────────────

export interface TextExpressionInputProps {
  /** Template string with `{fieldName}` tokens, e.g. `"Serial: {sn}, Type: {type}"`. */
  value?: string;
  onChange?: (value: string) => void;
  /** Available field references shown in the `@` suggestion dropdown. */
  items: MentionItem[];
  label?: string;
  description?: string;
  required?: boolean;
  error?: string;
  size?: 'sm' | 'lg';
  readOnly?: boolean;
}

/**
 * A single-line rich text editor for building text expressions.
 *
 * - Type free text anywhere.
 * - Type `@` to open a suggestion dropdown of available field references.
 * - Each field reference is stored as `{fieldName}` in the template string.
 * - Already-configured placeholders are highlighted as inline chips on mount.
 */
export default function TextExpressionInput({
  value = '',
  onChange,
  items,
  label,
  description,
  required = false,
  error,
  size = 'sm',
  readOnly = false,
}: TextExpressionInputProps) {
  const { classes } = useStyles({ size });

  // Keep items accessible inside editor callbacks without recreating the editor.
  const itemsRef = useRef<MentionItem[]>(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // Tracks the last value emitted by the editor to avoid update loops.
  const lastEmittedRef = useRef(value);

  const editor = useEditor({
    editable: !readOnly,
    extensions: [
      StarterKit.configure({
        blockquote: false,
        bold: false,
        bulletList: false,
        code: false,
        codeBlock: false,
        heading: false,
        horizontalRule: false,
        italic: false,
        listItem: false,
        orderedList: false,
        strike: false,
        hardBreak: false,
      }),
      PreventNewLines,
      MentionSelectionHighlight,
      Mention.configure({
        HTMLAttributes: { class: 'te-mention' },
        renderLabel: ({ node }) => node.attrs.label ?? node.attrs.id,
        suggestion: {
          char: '@',
          // Allow @ to be triggered after any character, not just whitespace.
          allowedPrefixes: null,
          // Override the default command which appends a trailing space.
          command: ({ editor, range, props }) => {
            editor
              .chain()
              .focus()
              .insertContentAt(range, [{ type: 'mention', attrs: props }])
              .run();
          },
          items: ({ query }) => {
            /** Strips accents and lowercases a string for comparison. */
            const normalize = (s: string) => removeAccents(s).toLowerCase();
            const normalizedQuery = normalize(query);
            return itemsRef.current.filter((item) => normalize(item.label).includes(normalizedQuery)).slice(0, 20);
          },
          render: () => {
            let renderer: ReactRenderer<MentionSuggestionListRef>;
            let popup: TippyInstance[];

            return {
              onStart(props) {
                renderer = new ReactRenderer(MentionSuggestionList, {
                  props,
                  editor: props.editor,
                });
                if (!props.clientRect) return;
                popup = tippy('body', {
                  getReferenceClientRect: props.clientRect as () => DOMRect,
                  appendTo: () => document.body,
                  content: renderer.element,
                  showOnCreate: true,
                  interactive: true,
                  trigger: 'manual',
                  placement: 'bottom-start',
                }) as TippyInstance[];
              },
              onUpdate(props) {
                renderer.updateProps(props);
                if (!props.clientRect) return;
                popup?.[0]?.setProps({
                  getReferenceClientRect: props.clientRect as () => DOMRect,
                });
              },
              onKeyDown(props) {
                if (props.event.key === 'Escape') {
                  popup?.[0]?.hide();
                  return true;
                }
                return renderer.ref?.onKeyDown(props) ?? false;
              },
              onExit() {
                popup?.[0]?.destroy();
                renderer.destroy();
              },
            };
          },
        },
      }),
    ],
    content: parseTemplateToContent(value, items),
    onUpdate: ({ editor }) => {
      if (!onChange) return;
      const template = serializeEditorToTemplate(editor);
      lastEmittedRef.current = template;
      onChange(template);
    },
  });

  // Sync externally-changed value (e.g. form reset) into the editor.
  useEffect(() => {
    if (!editor) return;
    if (value === lastEmittedRef.current) return;
    editor.commands.setContent(parseTemplateToContent(value, itemsRef.current));
    lastEmittedRef.current = value;
  }, [value, editor]);

  // Propagate readOnly changes.
  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!readOnly);
  }, [readOnly, editor]);

  return (
    <Input.Wrapper label={label} required={required} description={description} error={error}>
      <div className={classes.editorWrapper} onClick={() => editor?.commands.focus()}>
        <EditorContent editor={editor} />
      </div>
      {!readOnly && items.length > 0 && (
        <Text className={classes.hint}>
          Píšte text. Referenciu na pole vložíte pomocou <strong>@</strong>.
        </Text>
      )}
    </Input.Wrapper>
  );
}
